import { EventLabel } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { Stack, TagGroup } from '@cad-arc';
import LabelPill from '../../../common/components/label-pill';
import useDimensions from 'react-cool-dimensions';
import { Tooltip } from '../../../common/components/tooltip';
import {
    CautionLevelAttrId,
    EventQueueRowData,
    ScheduledEventQueueRowData,
    UnitQueueRowData,
} from '../../../socket/stream-types';
import { getVisibleNumberOfItems } from '../../utils/get-visible-number-of-items';
import { BaseQueueCell } from '../base-queue-cell';
import { getEventQueueRowDataOrScheduledEventQueueRowData } from '../../utils/get-event-queue-row-data';
import { locationCautionPriorityToLevel } from '../../../common/helpers/styles';

const CHAR_WIDTH = 9;
function getApproximateStringWidth(str: string): number {
    return str.length * CHAR_WIDTH;
}

function getNumToDisplay(containerWidth: number, eventLabels: EventLabel[]): number {
    return getVisibleNumberOfItems(containerWidth, eventLabels, (eventLabel) =>
        getApproximateStringWidth(eventLabel.name)
    );
}

const getLabels = (eventLabels: EventLabel[]): React.ReactNode =>
    eventLabels
        ? eventLabels.map((eventLabel) => {
              return (
                  <LabelPill
                      key={eventLabel.name}
                      text={eventLabel.name}
                      priority={locationCautionPriorityToLevel(
                          eventLabel.cautionLevelAttrId as CautionLevelAttrId
                      )}
                  />
              );
          })
        : null;

export const EventLabelsCell = observer(function EventLabelsCell({
    data,
}: {
    data: EventQueueRowData | UnitQueueRowData | ScheduledEventQueueRowData;
}): React.ReactElement {
    const dataToUse = getEventQueueRowDataOrScheduledEventQueueRowData(data);
    const { observe, width } = useDimensions();

    if (!dataToUse) {
        return <span />;
    }

    const unfilteredEventLabels = dataToUse.eventLabels ?? [];

    const numToDisplay = Math.max(1, getNumToDisplay(width - 10, unfilteredEventLabels));
    const labelsToDisplay = unfilteredEventLabels.slice(0, numToDisplay);
    const labelsBehindTip = unfilteredEventLabels.slice(numToDisplay);

    if (!dataToUse.eventLabels || dataToUse.eventLabels.length === 0) {
        return <BaseQueueCell />;
    }

    return (
        <BaseQueueCell display="flex" flexDirection="row" margin="0 -3px" ref={observe}>
            <Tooltip overlay={<TagGroup>{getLabels(unfilteredEventLabels)}</TagGroup>}>
                <Stack direction="row">
                    <TagGroup wrap="nowrap">{getLabels(labelsToDisplay)}</TagGroup>
                    {labelsBehindTip.length > 0 && <span>...</span>}
                </Stack>
            </Tooltip>
        </BaseQueueCell>
    );
});
